import type { EvalResultSeries } from "./evalCompareModel";
import type { EvalMergedResult } from "./evalCompareMergeModel";
import type { CompareStatGroup } from "./evalCompareSummaryModel";

function csvCell(value: string): string {
  const guarded =
    /^[=+@]/.test(value) || (/^-/.test(value) && !/^-(\d|\.)/.test(value)) ? `'${value}` : value;
  return /[",\n\r]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

export function summaryCsvFilename(count: number): string {
  return `evaluation-comparison-summary-${count}-runs.csv`;
}

export function mergedSummaryCsvFilename(count: number): string {
  return `evaluation-merge-summary-${count}-runs.csv`;
}

export function policyCompareSummaryCsvFilename(count: number): string {
  return `evaluation-policy-comparison-summary-${count}-policies.csv`;
}

export function buildSummaryCsv(
  items: readonly EvalResultSeries[],
  groups: readonly CompareStatGroup[],
): string {
  const rows = [
    [
      "Metric",
      ...items.map((item) => {
        const identity =
          item.label === item.shortLabel ? item.label : `${item.label} ${item.shortLabel}`;
        return `${identity} ${item.sublabel}`;
      }),
    ],
    ...groups.flatMap((group) =>
      group.columns.map((column) => [
        group.header !== null ? `${group.header} ${column.label}` : column.label,
        ...items.map((item) => {
          const value = column.value(item);
          return value === null ? "N/A" : column.format(value);
        }),
      ]),
    ),
  ];
  return `${rows.map((row) => row.map(csvCell).join(",")).join("\n")}\n`;
}

export function buildSeedStabilityCsv(result: EvalMergedResult): string {
  const duplicateCount = result.exclusions.filter(
    (exclusion) => exclusion.kind === "duplicate_seed",
  ).length;
  const missingCount = result.exclusions.filter(
    (exclusion) => exclusion.kind === "missing_seed",
  ).length;
  const episodesPerSeed =
    result.episodesPerSeed === null
      ? "N/A"
      : result.episodesPerSeed.min === result.episodesPerSeed.max
        ? String(result.episodesPerSeed.min)
        : `${result.episodesPerSeed.min}-${result.episodesPerSeed.max}`;
  const rows = [
    [],
    ["Seed Configuration", "Value"],
    ["Selected runs", String(result.selectedRunCount)],
    ["Included runs", String(result.includedRunCount)],
    ["Unique simulation seeds", String(result.uniqueSeedCount)],
    ["Pooled episodes", String(result.episodeCount)],
    ["Episodes per seed", episodesPerSeed],
    ["Excluded duplicate seeds", String(duplicateCount)],
    ["Excluded missing seeds", String(missingCount)],
    [],
    ["Seed Stability", "Statistic", "Mean", "SD", "Min", "Max", "Seeds"],
    ...result.stabilityRows.map((row) => [
      row.group,
      row.statistic,
      row.stats.mean === null ? "N/A" : row.format(row.stats.mean),
      row.stats.sampleSd === null ? "N/A" : row.formatSpread(row.stats.sampleSd),
      row.stats.min === null ? "N/A" : row.format(row.stats.min),
      row.stats.max === null ? "N/A" : row.format(row.stats.max),
      String(row.stats.count),
    ]),
  ];
  return `${rows.map((row) => row.map(csvCell).join(",")).join("\n")}\n`;
}
